import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useOnboarding } from "../context/OnboardingContext";
import { ONBOARDING_PATH } from "../lib/onboarding";
import { LoadingSplash } from "./kit/LoadingSplash";

/**
 * Sends a player who has not been through first-run onboarding to the
 * walkthrough (issue #289), and nobody else anywhere.
 *
 * WHY A GATE RATHER THAN A `nav()` IN `Register.jsx`. The ticket describes
 * the symptom at registration — `Register.jsx:118` sent a brand-new account
 * straight to an empty dashboard — but registration is only one of three
 * ways a player arrives at the app with the walk unfinished. The other two
 * are signing in again after abandoning it (slice 2's whole point) and
 * reopening a tab whose session cookie is still valid, which goes through no
 * form at all. One gate on the protected layout covers all three; three
 * `nav()` calls would have to agree with each other forever, and the third
 * has nowhere to live. So `Register.jsx` and `Login.jsx` are untouched by
 * this issue: they still navigate to `safeRedirectTarget(state.from)` and
 * this gate intercepts.
 *
 * HOW IT COMPOSES WITH #257's DEEP LINKS. A visitor who followed a shared
 * link and had no account reaches `/register` carrying that destination, and
 * `Register.jsx` navigates to it on success. This gate then redirects to the
 * walkthrough carrying the SAME location as `state.from`, and the
 * walkthrough hands control back to it on finish or skip (see
 * `Onboarding.jsx`). So: **for a brand-new registration the walkthrough
 * wins, and the deep link wins the moment the walkthrough is done with it.**
 * The link is not discarded — which is #257's whole complaint — it is
 * deferred by two screens. That ordering is deliberate: someone who has just
 * created an account has never seen this product, and the shared page is
 * still there thirty seconds later; whereas a first run that starts on
 * someone else's deep-linked page is a first impression nobody chose.
 *
 * WHAT IT NEVER DOES:
 *   - it never fires for a coach or admin. `useOnboarding` makes no request
 *     and reports nothing to do for a non-player, so this is not a role
 *     check that could rot — there is no state for it to be wrong about.
 *   - it never fires for a player who finished or skipped, or for one who
 *     was using this app before the walk existed (`api/src/lib/onboarding.ts`
 *     decides all three; this component only obeys).
 *   - it never redirects away FROM the walkthrough — `/onboarding` renders
 *     for any player who asks for it, which is what lets the Dashboard's
 *     resume prompt link straight there after a skip.
 *
 * WHY IT BLOCKS ON THE FIRST ANSWER. Rendering the dashboard and then
 * redirecting would show a player their empty dashboard for a beat — the
 * exact screen this issue exists to stop them landing on — and would make
 * the responsive harness measure whichever page won the race. It reuses
 * `ProtectedRoute`'s spinner and copy rather than inventing a second
 * loading language for the same half-second.
 */
export default function OnboardingGate({ children }) {
  const { user } = useAuth();
  const { required, step, loading } = useOnboarding();
  const location = useLocation();

  if (user?.role !== "player") return children;

  if (loading) {
    return (
      <LoadingSplash testId="onboarding-loading" />
    );
  }

  if (!required) return children;
  if (location.pathname.toLowerCase().replace(/\/+$/, "") === ONBOARDING_PATH) return children;

  return (
    <Navigate
      to={`${ONBOARDING_PATH}?step=${encodeURIComponent(step || "profile")}`}
      state={{ from: location }}
      replace
    />
  );
}
